import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "@/firebase";

interface DocumentTemplate {
  id: string;
  title: string;
  body: string;
  body2?: string;
  body3?: string;
  body4?: string;
  body5?: string;
  bodies?: string[];
  sortNo?: number;
  templateType?: string;
  paymentMethod?: string;
  course?: string;
}

const emptyForm = {
  title: "",
  pages: [""],
  sortNo: "",
  templateType: "shipping_paper",
  paymentMethod: "default",
  course: "default",
};

const buildPaymentSummaryFields = (paymentSummary?: Record<string, unknown>): string[] => {
  const fields: string[] = ["method", "type", "installmentCount", "amounts.total"];
  const schedule = Array.isArray(paymentSummary?.schedule) ? paymentSummary?.schedule : [];
  const scheduleCount = schedule.length ? Math.min(schedule.length, 3) : 3;

  for (let index = 0; index < scheduleCount; index += 1) {
    fields.push(`schedule[${index}].label`, `schedule[${index}].dueDate`, `schedule[${index}].amount`);
  }

  return Array.from(new Set(fields));
};

export default function DocumentTemplatePage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [form, setForm] = useState({
    title: "",
    pages: [""],
    sortNo: "",
    templateType: "shipping_paper",
    paymentMethod: "default",
    course: "default",
  });
  const [isVariablePanelOpen, setIsVariablePanelOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<string>("");
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [selectedField, setSelectedField] = useState<string>("");
  const [fieldLoading, setFieldLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const templateQuery = query(collection(db, "dc_templates"), orderBy("sortNo", "asc"));
      const snapshot = await getDocs(templateQuery);
      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<DocumentTemplate, "id">),
      }));
      setTemplates(data);
      if (data.length === 0) {
        setSelectedId("");
        setForm(emptyForm);
      }
    } catch (err) {
      console.error("Failed to load document templates", err);
      setError("テンプレートの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    const templatePages = Array.isArray(selectedTemplate.bodies) && selectedTemplate.bodies.length
      ? selectedTemplate.bodies
      : [
          selectedTemplate.body || "",
          selectedTemplate.body2 || "",
          selectedTemplate.body3 || "",
          selectedTemplate.body4 || "",
          selectedTemplate.body5 || "",
        ].filter((value, index, array) => {
          if (value) return true;
          return array.slice(index + 1).some((later) => later);
        });
    setForm({
      title: selectedTemplate.title || "",
      pages: templatePages.length ? templatePages : [""],
      sortNo: selectedTemplate.sortNo?.toString() || "",
      templateType: selectedTemplate.templateType || "shipping_paper",
      paymentMethod: selectedTemplate.paymentMethod || "default",
      course: selectedTemplate.course || "default",
    });
  }, [selectedTemplate]);

  const templateTypeOptions = [{ value: "shipping_paper", label: "発送用紙" }];

  const paymentMethodOptions = [
    { value: "default", label: "共通(デフォルト)" },
    { value: "クレジット", label: "クレジット" },
    { value: "スマホ決済", label: "スマホ決済" },
    { value: "後払い", label: "後払い" },
    { value: "後払い分割", label: "後払い分割" },
    { value: "コンビニ決済", label: "コンビニ決済" },
  ];

  const courseOptions = [
    { value: "default", label: "共通(デフォルト)" },
    { value: "通信講座＋試験", label: "通信講座＋試験" },
    { value: "試験のみ", label: "試験のみ" },
  ];

  const collectionOptions = [
    { value: "Order", label: "Order", templateKey: "order" },
    { value: "User", label: "User", templateKey: "user" },
    { value: "Shikaku", label: "Shikaku", templateKey: "shikaku" },
    { value: "paymentSummary", label: "paymentSummary", templateKey: "paymentSummary" },
  ];

  const selectedCollectionOption =
    collectionOptions.find((option) => option.value === selectedCollection) || null;

  useEffect(() => {
    const loadFields = async () => {
      if (!selectedCollection) {
        setAvailableFields([]);
        setSelectedField("");
        return;
      }
      setFieldLoading(true);
      try {
        if (selectedCollection === "paymentSummary") {
          const fieldQuery = query(collection(db, "Order"), limit(1));
          const snapshot = await getDocs(fieldQuery);
          const data = snapshot.empty
            ? undefined
            : (snapshot.docs[0].data().paymentSummary as Record<string, unknown> | undefined);
          const fields = buildPaymentSummaryFields(data);
          setAvailableFields(fields);
          setSelectedField(fields[0] || "");
          return;
        }

        const fieldQuery = query(collection(db, selectedCollection), limit(1));
        const snapshot = await getDocs(fieldQuery);
        if (snapshot.empty) {
          setAvailableFields([]);
          setSelectedField("");
          return;
        }
        const data = snapshot.docs[0].data() as Record<string, unknown>;
        const fields = Object.keys(data).sort();
        setAvailableFields(fields);
        setSelectedField(fields[0] || "");
      } catch (err) {
        console.error("Failed to load template fields", err);
        setAvailableFields([]);
        setSelectedField("");
      } finally {
        setFieldLoading(false);
      }
    };

    loadFields();
  }, [selectedCollection]);

  const handleNew = () => {
    setSelectedId("");
    setForm(emptyForm);
  };

  const addPage = () => {
    setForm((prev) => ({ ...prev, pages: [...prev.pages, ""] }));
  };

  const removePage = (index: number) => {
    setForm((prev) => ({
      ...prev,
      pages: prev.pages.filter((_, pageIndex) => pageIndex !== index),
    }));
  };

  const updatePage = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      pages: prev.pages.map((page, pageIndex) => (pageIndex === index ? value : page)),
    }));
  };

  const handleSave = async () => {
    const title = form.title.trim();
    const pages = form.pages.map((page) => page.trim());
    const templateType = form.templateType.trim();
    const paymentMethod = form.paymentMethod.trim();
    const course = form.course.trim();
    const sortNoValue = form.sortNo.toString().trim();
    const sortNo = sortNoValue ? Number(sortNoValue) : 0;
    const hasAnyBody = pages.some((page) => page);
    if (!title || !hasAnyBody) {
      setError("タイトル・本文は必須です。");
      return;
    }
    if (!templateType) {
      setError("テンプレート種類は必須です。");
      return;
    }
    if (sortNoValue && Number.isNaN(sortNo)) {
      setError("SortNo は数値で入力してください。");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const [body, body2, body3, body4, body5] = pages;
      const payload = {
        title,
        body: body || "",
        body2: body2 || null,
        body3: body3 || null,
        body4: body4 || null,
        body5: body5 || null,
        bodies: pages.filter((page) => page),
        sortNo,
        templateType,
        paymentMethod: paymentMethod || "default",
        course: course || "default",
      };
      if (selectedId) {
        await updateDoc(doc(db, "dc_templates", selectedId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "dc_templates"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      await loadTemplates();
      if (!selectedId) {
        setForm(emptyForm);
      }
    } catch (err) {
      console.error("Failed to save document template", err);
      setError("テンプレートの保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() => navigate("/admin/templates/mail")}
            >
              ← テンプレート管理に戻る
            </button>
            <h1 className="mt-2 text-2xl font-bold text-gray-800">書類テンプレート管理</h1>
            <p className="text-sm text-gray-500">
              テンプレート本文には {"{{"}変数{"}}"} を含められます。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="rounded border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50"
              onClick={() => setIsVariablePanelOpen((prev) => !prev)}
            >
              引用変数
            </button>
            <button
              className="rounded border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-600 shadow-sm hover:bg-emerald-50"
              onClick={() => setIsPreviewOpen((prev) => !prev)}
            >
              プレビュー
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
              onClick={handleNew}
            >
              新規作成
            </button>
          </div>
        </div>

        {error && <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

        <div className="grid gap-6 lg:grid-cols-[280px,1fr]">
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="mb-3 text-sm font-semibold text-gray-700">テンプレート一覧</div>
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedId(template.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                    selectedId === template.id
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-100 text-gray-700 hover:border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-semibold">{template.title}</div>
                  <div className="text-xs text-gray-400">SortNo: {template.sortNo ?? 0}</div>
                  <div className="text-xs text-gray-500 truncate">{template.templateType || "shipping_paper"}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {template.paymentMethod || "default"} / {template.course || "default"}
                  </div>
                </button>
              ))}
              {templates.length === 0 && (
                <div className="rounded border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
                  テンプレートがありません
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700" htmlFor="template-title">
                  タイトル
                </label>
                <input
                  id="template-title"
                  type="text"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700" htmlFor="template-sort-no">
                  SortNo
                </label>
                <input
                  id="template-sort-no"
                  type="number"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={form.sortNo}
                  onChange={(event) => setForm((prev) => ({ ...prev, sortNo: event.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700" htmlFor="template-type">
                  テンプレート種類
                </label>
                <select
                  id="template-type"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={form.templateType}
                  onChange={(event) => setForm((prev) => ({ ...prev, templateType: event.target.value }))}
                >
                  {templateTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700" htmlFor="template-payment-method">
                  支払方法
                </label>
                <select
                  id="template-payment-method"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={form.paymentMethod}
                  onChange={(event) => setForm((prev) => ({ ...prev, paymentMethod: event.target.value }))}
                >
                  {paymentMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-1 text-xs text-gray-400">
                  デフォルトは支払方法が一致しない場合のフォールバックとして使われます。
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700" htmlFor="template-course">
                  コース
                </label>
                <select
                  id="template-course"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={form.course}
                  onChange={(event) => setForm((prev) => ({ ...prev, course: event.target.value }))}
                >
                  {courseOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-1 text-xs text-gray-400">
                  デフォルトはコースが一致しない場合のフォールバックとして使われます。
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700" htmlFor="template-body">
                本文（複数ページ）
              </label>
              <div className="space-y-4">
                {form.pages.map((page, index) => (
                  <div key={`page-${index}`} className="rounded-lg border border-gray-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs font-semibold text-gray-600">ページ {index + 1}</div>
                      {form.pages.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-500 hover:underline"
                          onClick={() => removePage(index)}
                        >
                          削除
                        </button>
                      )}
                    </div>
                    <textarea
                      className="min-h-[240px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      value={page}
                      onChange={(event) =>
                        updatePage(index, event.target.value)
                      }
                      placeholder={
                        index === 0
                          ? "<style>\nbody { font-family: sans-serif; }\n</style>\n<div>\n  <h1>発送用紙</h1>\n  <p>{{ user.display_name }} 様</p>\n</div>"
                          : "<div>\n  <h2>続きのページ</h2>\n</div>"
                      }
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="rounded border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={addPage}
                >
                  ＋ ページを追加
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                HTML形式で記述できます。必要に応じて {"<style>"} タグでCSSを含めてください。
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                onClick={handleNew}
              >
                クリア
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "保存中..." : "保存"}
              </button>
            </div>
          </div>
        </div>

        {isVariablePanelOpen && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="text-sm font-semibold text-blue-700">引用変数の追加</div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="rounded border border-blue-200 px-3 py-2 text-sm"
                  value={selectedCollection}
                  onChange={(event) => setSelectedCollection(event.target.value)}
                >
                  <option value="">コレクション選択</option>
                  {collectionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded border border-blue-200 px-3 py-2 text-sm"
                  value={selectedField}
                  onChange={(event) => setSelectedField(event.target.value)}
                  disabled={!selectedCollection || fieldLoading}
                >
                  {fieldLoading && <option value="">Loading...</option>}
                  {!fieldLoading &&
                    availableFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                </select>
                <button
                  className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  type="button"
                  disabled={!selectedCollectionOption || !selectedField}
                  onClick={() => {
                    if (!selectedCollectionOption || !selectedField) return;
                    const variableToken = `{{ ${selectedCollectionOption.templateKey}.${selectedField} }}`;
                    setForm((prev) => {
                      const targetIndex = prev.pages.length - 1;
                      const nextPages = prev.pages.map((page, index) =>
                        index === targetIndex ? `${page}\n${variableToken}`.trim() : page
                      );
                      return { ...prev, pages: nextPages };
                    });
                  }}
                >
                  追加
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs text-blue-700">
              例: {"{{"} order.trackingNumbers {"}}"} の形式で本文に挿入されます。
            </div>
          </div>
        )}

        {isPreviewOpen && (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-emerald-700">プレビュー（A4）</div>
              <button
                type="button"
                className="rounded border border-emerald-200 px-3 py-1 text-xs text-emerald-700 hover:bg-emerald-100"
                onClick={() => setIsPreviewOpen(false)}
              >
                閉じる
              </button>
            </div>
            <div className="overflow-auto rounded-lg border border-emerald-200 bg-white p-4">
              <div className="mx-auto min-h-[297mm] w-[210mm] bg-white p-6 shadow">
                {form.pages.map((page, index) => (
                  <div key={`preview-${index}`} className={index > 0 ? "mt-8" : ""}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: page || "<p>本文が未入力です。</p>",
                      }}
                    />
                    {index < form.pages.length - 1 && <div className="mt-6 border-t border-dashed border-gray-200" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
